import React, { useEffect, useRef, useState } from "react";

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import moment from "moment";
import { useSelector } from "react-redux";
import { selectDevice, selectRoom, selectUserData } from "../../features/store";
import { useSnackbar } from "notistack";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { getPeopleCountLog } from "../../apis/PeopleCount.Api";

const PeopleFlow = ({ currBuilding }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [index, setIndex] = useState();
    const [tabUnderlineWidth, setTabUnderlineWidth] = useState(0);
    const [tabUnderlineLeft, setTabUnderlineLeft] = useState(0);
    const [displayData, setDisplayData] = useState();
    const devices = useSelector(selectDevice);
    const rooms = useSelector(selectRoom);
    const [selectedRoom, setSelectedRoom] = useState("all"); // id of selected room
    const userData = useSelector(selectUserData);

    useEffect(() => {
        setIndex(0);
    }, []);

    const tabsRef = useRef([]);

    useEffect(() => {
        const currentTab = tabsRef.current[index];
        setTabUnderlineLeft(currentTab?.offsetLeft ?? 0);
        setTabUnderlineWidth(currentTab?.clientWidth ?? 0);
        getDisplayData(index);
    }, [index, currBuilding, selectedRoom]);

    async function getDisplayData(index) {
        let deviceToQuery = null;
        if (selectedRoom !== "all") {
            deviceToQuery = Object.keys(devices).filter(
                (did) => did.toUpperCase().startsWith("P") && devices[did].roomId === selectedRoom
            );
            if (!deviceToQuery.length) {
                deviceToQuery = null;
            }
        }
        let arr = ["today", "yesterday", "week", "month"];
        let mode = arr[index];
        // get cumulative count by hour
        const now = new Date();
        let today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        let yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);

        let weekAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
        let monthAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
        const startTime = {
            today: today,
            yesterday: yesterday,
            week: weekAgo,
            month: monthAgo,
        };
        const endTime = {
            today: now,
            yesterday: today,
            week: today,
            month: today,
        };
        let start = moment(startTime[mode]).format("YYMMDDHHmmssSSS");
        let end = moment(endTime[mode]).format("YYMMDDHHmmssSSS");

        let payload;
        if (deviceToQuery) {
            payload = {
                device_id: JSON.stringify(deviceToQuery),
                start_time: start,
                end_time: end,
            };
        } else {
            payload = {
                start_time: start,
                end_time: end,
            };
        }
        let res = await getPeopleCountLog(payload);
        if (!res.data.indicator) {
            enqueueSnackbar(res.data.message, { variant: "error" });
            return;
        }
        let counts = res.data.message;
        let intervals = new Array(24).fill(0);
        for (let count of counts) {
            // filter
            let rid = count.room_id;
            if (rooms[rid]?.buildingId !== currBuilding?.buildingId) continue;
            let time = moment(count.update_time, "YYMMDDHHmmssSSS").toDate();
            let hour = time.getHours();
            intervals[hour] += count.entry;
        }

        let data = [];
        for (let i = 0; i < 24; i++) {
            const time = i.toString().padStart(2, "0") + ":00";
            data.push({ time: time, count: intervals[i], range: i });
        }
        setDisplayData(data);
    }

    return (
        <div className="people-flow">
            <div className="people-flow-header"></div>{" "}
            <div className="people-flow-tabs">
                {["Today", "Yesterday", "Last Week", "Last Month"].map((title, ind) => (
                    <div
                        key={title}
                        className={"tab"}
                        onClick={() => {
                            setIndex(ind);
                        }}
                        ref={(el) => (tabsRef.current[ind] = el)}
                    >
                        {title}
                    </div>
                ))}
                <FormControl sx={{ width: 180 }} size="small" className="select-room">
                    <InputLabel id="select-room">Select Room</InputLabel>

                    <Select
                        id="select-room"
                        value={selectedRoom}
                        onChange={(e) => {
                            setSelectedRoom(e.target.value);
                        }}
                        labelId="select-room"
                        label="Select Room"
                    >
                        {["all", ...Object.keys(rooms)]
                            .filter((rid) => rid === "all" || rooms[rid].buildingId === currBuilding.buildingId)
                            .sort((a, b) => {
                                if (a === "all") return -1;
                                if (b === "all") return 1;
                                return rooms[a].roomName.localeCompare(rooms[b].roomName, undefined, {
                                    sensitivity: "base",
                                });
                            })
                            .map((rid, i) => (
                                <MenuItem value={rid} key={rid}>
                                    {rid === "all" ? "All" : rooms[rid].roomName}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>
            </div>
            <div className="underline" style={{ left: tabUnderlineLeft, width: tabUnderlineWidth }} />
            <div className="hline line"></div>
            <div className="graphWrapper">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        width={500}
                        height={300}
                        data={displayData}
                        margin={{
                            top: 2,
                            right: 30,
                            left: 20,
                            bottom: 2,
                        }}
                        barCategoryGap={1}
                    >
                        <Tooltip
                            labelFormatter={(label) =>
                                label.toString().padStart(2, "0") +
                                ":00" +
                                "-" +
                                (label + 1).toString().padStart(2, "0") +
                                ":00"
                            }
                            wrapperStyle={{ backgroundColor: "red", borderColor: "green" }}
                        />

                        <XAxis dataKey="range" hide />
                        <XAxis
                            dataKey="time"
                            scale={"band"}
                            xAxisId="values"
                            padding={{ right: 0, left: 0 }}
                            tick={{ fontSize: 14 }}
                            interval={"equidistantPreserveStart"}
                        />
                        <CartesianGrid strokeDasharray="3 3" vertical={0} />
                        <YAxis label={{ value: "Count", angle: -90, position: "insideLeft" }} />

                        <Legend />
                        <Bar dataKey="count" fill={"#122a65"} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default PeopleFlow;
